/**
 * Reply / reply-all / forward draft builders (spec §10.2). Pure: the caller supplies the
 * rendered body text (`innerText` of the shadow root) and the formatted date.
 */
import type { Address, ComposeDraft, ForwardPart } from '$lib/types';

export const RE_PREFIX_RE = /^\s*(re|رد)\s*(\[\d+\])?\s*:/i;
export const FWD_PREFIX_RE = /^\s*(fwd?|إعادة توجيه|مُمرَّرة)\s*:/i;

/** Lines of the quoted body kept in a reply/forward. */
export const QUOTE_MAX_LINES = 200;

const lower = (s: string | null | undefined) => (s || '').trim().toLowerCase();

export function reSubject(subject: string | null | undefined): string {
  return RE_PREFIX_RE.test(subject || '') ? subject || '' : 'Re: ' + (subject || '');
}

export function fwdSubject(subject: string | null | undefined): string {
  return FWD_PREFIX_RE.test(subject || '') ? subject || '' : 'Fwd: ' + (subject || '');
}

/** Reply target = `replyTo[0] ?? from[0]` (address only). */
export function replyTarget(from: readonly Address[], replyTo?: readonly Address[] | null): string {
  const list = replyTo && replyTo.length ? replyTo : from;
  return list[0]?.address || '';
}

/** Case-insensitive de-dup keeping first spelling, skipping addresses already in `seen`. */
export function dedupeAddresses(addrs: readonly string[], seen: Iterable<string> = []): string[] {
  const taken = new Set<string>([...seen].map(lower));
  const out: string[] = [];
  for (const a of addrs) {
    const s = (a || '').trim();
    if (!s) continue;
    const k = lower(s);
    if (taken.has(k)) continue;
    taken.add(k);
    out.push(s);
  }
  return out;
}

/**
 * Reply-all recipients: `to` = reply target unless it is me; `cc` = deduped to + cc minus me and
 * minus the target. If the target is me and others exist, the first other becomes `to`.
 */
export function replyAllRecipients(
  from: readonly Address[],
  replyTo: readonly Address[] | null | undefined,
  to: readonly Address[],
  cc: readonly Address[],
  me: string,
): { to: string; cc?: string } {
  const target = replyTarget(from, replyTo);
  const exclude = new Set<string>();
  if (me) exclude.add(lower(me));
  const primary = target && lower(target) !== lower(me) ? target : '';
  if (primary) exclude.add(lower(primary));
  const others = dedupeAddresses([...to, ...cc].map((a) => a.address), exclude);
  if (!primary && others.length) return { to: others[0]!, cc: others.slice(1).join(', ') || undefined };
  if (!primary && !others.length) return { to: target };
  return { to: primary, cc: others.join(', ') || undefined };
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Quoted block: grey header line + `<blockquote>` of the first 200 lines, escaped, `<br>`-joined. */
export function quoteHtml(header: string, bodyText: string): string {
  const lines = (bodyText || '').split('\n').slice(0, QUOTE_MAX_LINES).map(esc).join('<br>');
  return `<div style="color:#8a93a3;font-size:13px">${esc(header)}</div><blockquote>${lines}</blockquote>`;
}

/** `references = [...references, messageId]` when a Message-ID exists. */
export function threadReferences(messageId: string | null | undefined, references: readonly string[] | null | undefined): string[] {
  return messageId ? [...(references ?? []), messageId] : [...(references ?? [])];
}

export interface ReplySource {
  subject: string;
  from: readonly Address[];
  replyTo?: readonly Address[] | null;
  to: readonly Address[];
  cc: readonly Address[];
  messageId: string | null;
  references: readonly string[];
  /** Fallback sender address when `from` is empty (list row). */
  fromAddr?: string;
}

/**
 * Build a reply draft. `quoted` is the already formatted quote html (see `quoteHtml`).
 * `all` switches to reply-all recipient computation.
 */
export function buildReplyDraft(src: ReplySource, opts: { me: string; all: boolean; quoted: string }): ComposeDraft {
  const from = src.from.length ? src.from : [{ name: '', address: src.fromAddr || '' }];
  const rcpt = opts.all
    ? replyAllRecipients(from, src.replyTo, src.to, src.cc, opts.me)
    : { to: replyTarget(from, src.replyTo), cc: undefined };
  return {
    to: rcpt.to,
    cc: rcpt.cc,
    subject: reSubject(src.subject),
    html: opts.quoted,
    inReplyTo: src.messageId ?? undefined,
    references: threadReferences(src.messageId, src.references),
  };
}

export interface ForwardSource {
  subject: string;
  attachments?: readonly { part: string; filename: string | null; size: number | null; type: string; inline?: boolean }[];
}

/** Forward draft: `fwdFrom` carries the non-inline named attachments to re-attach server-side. */
export function buildForwardDraft(src: ForwardSource, quoted: string, folder: string, uid: number): ComposeDraft {
  const parts: ForwardPart[] = (src.attachments ?? [])
    .filter((a) => !a.inline && a.filename)
    .map((a) => ({ part: a.part, filename: a.filename, size: a.size, type: a.type }));
  return {
    subject: fwdSubject(src.subject),
    html: quoted,
    fwdFrom: parts.length ? { folder, uid, parts } : undefined,
  };
}

/** Whether the message is from the logged-in user (case-insensitive address match). */
export function isFromMe(fromAddr: string | null | undefined, me: string | null | undefined): boolean {
  return !!me && lower(fromAddr) === lower(me);
}

/** Recipient role for the reader chip: `to` (my address in To), `cc`, `other`, or `""`. */
export function recipientRole(to: readonly Address[], cc: readonly Address[], me: string): 'to' | 'cc' | 'other' | '' {
  const m = lower(me);
  if (!m) return '';
  if (to.some((a) => lower(a.address) === m)) return 'to';
  if (cc.some((a) => lower(a.address) === m)) return 'cc';
  return to.length || cc.length ? 'other' : '';
}
